import { FC, useContext, useEffect, useMemo, useRef } from "react";
import { CountiesSelector } from "@app/projects/network-assessment/multi-map-export/components/CountiesSelector";
import { RegionSetStatusType } from "@app/projects/enumerators";
import { MultiMapExportAction } from "@app/projects/network-assessment/multi-map-export/state-management/multiMapExportStateManagement";
import { MultiMapExportContext } from "@app/projects/network-assessment/multi-map-export/MultiMapExportContext";
import { MultiMapExportSection } from "@app/projects/network-assessment/multi-map-export/components/MultiMapExportSection";
import { availableCountyStatuses, countySelectionFor, initialServiceAreaSelection, orderCountyStatuses, reconcileCountySelection, statesInScope } from "@app/projects/network-assessment/multi-map-export/utils/serviceAreaSelection";
import { useMultiMapExportServiceAreas } from "@app/projects/network-assessment/multi-map-export/hooks/useMultiMapExportServiceAreas";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import RoundCheckboxMultiSelect from "@app/shared/RoundCheckboxMultiSelect";

const SECTION_NUMBER = 2;

export const ServiceAreaSection: FC<React.PropsWithChildren<unknown>> = () => {
  const { launchContext, project, projectRunConfigurationId, selections, selectionsChanged } = useContext(MultiMapExportContext);
  const { isError, isLoading, isSuccess, states } = useMultiMapExportServiceAreas(project?.projectId, Number(projectRunConfigurationId));
  const hasInitialized = useRef(false);

  const selectedStatuses = selections?.selectedCountyStatuses ?? [];
  const selectedCountyRegionSetIds = selections?.selectedCountyRegionSetIds ?? [];
  const availableStatuses = useMemo(() => availableCountyStatuses(states), [states]);
  const scopedStates = useMemo(() => statesInScope(states, selectedStatuses), [states, selectedStatuses]);

  useEffect(() => {
    // Once per mount: a refetch must not discard edits the user has already made.
    if (hasInitialized.current || !isSuccess) return;

    hasInitialized.current = true;
    selectionsChanged?.({
      type: MultiMapExportAction.CountyStatusesChanged,
      value: initialServiceAreaSelection(states, launchContext),
    });
  }, [isSuccess, launchContext, selectionsChanged, states]);

  const handleStatusesChange = (nextStatuses: RegionSetStatusType[]) => selectionsChanged?.({
    type: MultiMapExportAction.CountyStatusesChanged,
    value: {
      ...countySelectionFor(states, reconcileCountySelection(states, selectedCountyRegionSetIds, selectedStatuses, nextStatuses)),
      selectedCountyStatuses: nextStatuses,
    },
  });

  const handleCountiesChange = (regionSetIds: number[]) => selectionsChanged?.({
    type: MultiMapExportAction.CountiesChanged,
    value: countySelectionFor(states, regionSetIds),
  });

  const renderBody = () => {
    if (isError) {
      return (
        <p className="multi-map-export__section-error" data-testid="multi-map-export__service-area-error">
          We couldn&apos;t load the counties for this project. Refresh the page to try again.
        </p>
      );
    }

    if (isLoading || !isSuccess) {
      return <LoadingSpinner />;
    }

    return (
      <>
        {availableStatuses.length > 0 && (
          <div className="multi-map-export__field">
            <RoundCheckboxMultiSelect
              label="County Status"
              onChange={(next) => handleStatusesChange(orderCountyStatuses(next))}
              selectedValues={[...selectedStatuses]}
              testId="multi-map-export__county-status"
              values={[...availableStatuses]}
            />
            <p className="multi-map-export__field-helper-text">Selected statuses set which counties are in scope.</p>
          </div>
        )}
        <CountiesSelector
          hasCounties={states.length > 0}
          onChange={handleCountiesChange}
          selectedCountyRegionSetIds={selectedCountyRegionSetIds}
          statesInScope={scopedStates}
        />
      </>
    );
  };

  return (
    <MultiMapExportSection
      sectionNumber={SECTION_NUMBER}
      subHeader="Select the counties you would like to create maps for."
      testId="multi-map-export__service-area-section"
      title="Service Area"
    >
      {renderBody()}
    </MultiMapExportSection>
  );
};

export default ServiceAreaSection;
